import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { subscriptionServices, subscriptionPlanServices } from '../../lib/supabase';
import type { Subscription, SubscriptionPlan } from '../../lib/supabase';

const SubscriptionsTab = () => {
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { id: customerId } = useParams<{ id: string }>();

  useEffect(() => {
    const fetchData = async () => {
      if (!customerId) {
        setError('No customer ID provided');
        setLoading(false);
        return;
      }

      try {
        // Fetch customer's subscriptions
        const { data: subsData, error: subsError } = await subscriptionServices.getByCustomer(customerId);
        if (subsError) throw subsError;

        // Fetch all subscription plans
        const { data: plansData, error: plansError } = await subscriptionPlanServices.getAll();
        if (plansError) throw plansError;

        setSubscriptions(subsData || []);
        setPlans(plansData || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch subscription data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [customerId]);

  const getStatusBadgeColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'trialing':
        return 'bg-yellow-100 text-yellow-800';
      case 'canceled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPlanDetails = (planId: string) => {
    return plans.find(plan => plan.id === planId);
  };

  if (loading) return <div className="text-center p-4">Loading...</div>;
  if (error) return <div className="text-red-600 p-4">{error}</div>;

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-4 py-5 sm:px-6">
        <h3 className="text-lg font-medium leading-6 text-gray-900">Subscriptions</h3>
        <p className="mt-1 max-w-2xl text-sm text-gray-500">
          View and manage customer subscriptions
        </p>
      </div>

      <div className="border-t border-gray-200">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Plan
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Start Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  End Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Price
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {subscriptions.map((subscription) => {
                const plan = getPlanDetails(subscription.plan_id);
                return (
                  <tr key={subscription.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {plan?.name || 'Unknown Plan'}
                        </div>
                        <div className="text-sm text-gray-500">
                          {plan?.description}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeColor(subscription.status)}`}>
                        {subscription.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(subscription.start_date).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {subscription.end_date ? new Date(subscription.end_date).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ${plan?.price || 0}/mo
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button className="text-blue-600 hover:text-blue-900 mr-4">Edit</button>
                      {!subscription.cancel_at_period_end && (
                        <button className="text-red-600 hover:text-red-900">Cancel</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {subscriptions.length === 0 && (
          <div className="text-center py-8">
            <p className="text-gray-500 text-sm">No subscriptions found</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionsTab;
